import React, { useState, useEffect } from "react";
import { Heart, ArrowRight } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import WhatsAppButton from "./WhatsAppButton";
import Footer from "./Footer";
import Header from "./Header";
import AddToCartButton from "./AddToCart";
import { collection, query, where, getDocs, addDoc } from "firebase/firestore";
import { db } from "../firebase";
import { getUserId } from "../utils/utils";

const CategoryPage = () => {
  const navigate = useNavigate();
  const { category, subcategory } = useParams();
  const userId = getUserId();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState({});
  useEffect(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }, []);
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        let productsQuery;
        
        if (subcategory) {
          productsQuery = query(
            collection(db, "products"),
            where("subCategory", "==", subcategory)
          );
        } else if (category) {
          productsQuery = query(
            collection(db, "products"),
            where("category.name", "==", category)
          );
        }

        const querySnapshot = await getDocs(productsQuery);
        const productsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          image: doc.data().images[0] // Using first image as main image
        }));
        
        setProducts(productsData);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching products:", error);
        setLoading(false);
      }
    };

    const fetchCategories = async () => {
      try {
        const categoriesSnapshot = await getDocs(collection(db, "categories"));
        const categoriesData = {};
        categoriesSnapshot.docs.forEach(doc => {
          const data = doc.data();
          categoriesData[data.name] = data.subCategories || [];
        });
        setCategories(categoriesData);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchProducts();
    fetchCategories();
  }, [category, subcategory]);

  const addToCart = async (product) => {
    try {
      const cartRef = collection(db, `users/${userId}/cart`);
      await addDoc(cartRef, {
        productId: product.id,
        name: product.name,
        price: product.price,
        image: product.image,
        quantity: 1
      });
    } catch (error) {
      console.error("Error adding to cart:", error);
      throw error;
    }
  };

  const bgPatterns = {
    Rings: "bg-gradient-to-r from-purple-100 to-pink-100",
    Necklaces: "bg-gradient-to-r from-purple-100 to-blue-100",
    Earrings: "bg-gradient-to-r from-purple-100 to-indigo-100",
    Bracelets: "bg-gradient-to-r from-purple-100 to-violet-100",
    Articles: "bg-gradient-to-r from-purple-100 to-fuchsia-100"
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-purple-50 via-white to-purple-50">
      <Header />

      {/* Category/Subcategory Hero Section */}
      <div className={`relative h-64 ${bgPatterns[category] || 'bg-gradient-to-r from-purple-100 to-pink-100'} overflow-hidden`}>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-purple-900/10" />
        <div className="relative container mx-auto px-6 h-full flex items-center">
          <div className="max-w-2xl">
            <h1 className="text-4xl font-bold mb-2 text-purple-900">
              {subcategory || category}
            </h1>
            <p className="text-xl text-purple-700">
              Discover our exclusive collection of {subcategory?.toLowerCase() || category?.toLowerCase()}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-16">
        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {products.map((product) => (
            <div
              key={product.id}
              className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
            >
              <div className="relative aspect-square overflow-hidden">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                />
                <button className="absolute top-4 right-4 p-2 bg-white/80 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                  <Heart className="w-5 h-5 text-purple-600 hover:text-red-500 transition-colors" />
                </button>
              </div>
              <div className="p-6">
                <h4 className="text-lg font-medium text-purple-900 mb-2">
                  {product.name}
                </h4>
                <p className="text-xl font-serif text-purple-800 mb-4">
                  ₹{product.price}
                </p>
                <AddToCartButton
                  onClick={(e) => {
                    e.stopPropagation();
                    addToCart(product);
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Other Categories Section */}
        {!subcategory && (
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-purple-900 mb-8">
              Explore Other Categories
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Object.entries(categories)
                .filter(([cat]) => cat !== category)
                .map(([cat, subCats]) => (
                  <div key={cat} className="bg-white rounded-xl shadow-md p-6">
                    <h3 className="text-xl font-bold text-purple-900 mb-4">{cat}</h3>
                    <ul className="space-y-2">
                      {subCats.map((subCat) => (
                        <li key={subCat}>
                          <button
                            onClick={() => navigate(`/category/${cat}/${subCat}`)}
                            className="text-purple-600 hover:text-purple-800 transition-colors flex items-center"
                          >
                            {subCat}
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>

      <WhatsAppButton />
      <Footer />
    </div>
  );
};

export default CategoryPage;